import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { AppConfig } from 'src/config/app.config';
import { AppNotification } from './notification.model';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly POLL_INTERVAL = 15000;
  private _apiUrl: string;
  private pollSub: Subscription | null = null;
  private seenIds = new Set<number>();
  private firstPoll = true;

  notifications$ = new BehaviorSubject<AppNotification[]>([]);
  unreadCount$ = new BehaviorSubject<number>(0);
  /** Emits only newly-arrived unread items (not present on the first poll). */
  newNotifications$ = new Subject<AppNotification[]>();

  constructor(private authService: AuthService, appConfig: AppConfig) {
    this._apiUrl = appConfig.baseUrl;
  }

  startPolling(): void {
    if (this.pollSub) return;
    this.pollSub = timer(0, this.POLL_INTERVAL)
      .pipe(
        switchMap(() => this._fetchNotifications())
      )
      .subscribe(response => {
        if (!response) return;
        const list: AppNotification[] = this._mapResponse(response);
        this._processIncoming(list);
      });
  }

  /** Force an immediate poll without waiting for the next timer tick. */
  pollNow(): void {
    this._fetchNotifications()
      .subscribe(response => {
        if (!response) return;
        const list: AppNotification[] = this._mapResponse(response);
        this._processIncoming(list);
      });
  }

  stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
    this.seenIds.clear();
    this.firstPoll = true;
    this.notifications$.next([]);
    this.unreadCount$.next(0);
  }

  markRead(id: number): void {
    this.authService.put(this._apiUrl + '/api/v1/UserNotifications/StatusUpdate', { id, statusId: 1 })
      .subscribe(() => {
        const updated = this.notifications$.value.map(n =>
          n.id === id ? { ...n, isRead: true } : n
        );
        this.notifications$.next(updated);
        this.unreadCount$.next(updated.filter(n => !n.isRead).length);
      });
  }

  markAllRead(): void {
    this.authService.put(this._apiUrl + '/api/v1/UserNotifications/ReadAll', {})
      .subscribe(() => {
        const updated = this.notifications$.value.map(n => ({ ...n, isRead: true }));
        this.notifications$.next(updated);
        this.unreadCount$.next(0);
      });
  }

  private _fetchNotifications() {
    const clientId = parseInt(atob(localStorage.getItem('CLIENT_ID') ?? '0'), 10);
    const userTypeId = parseInt(atob(localStorage.getItem('USER_BASE_TYPE_ID') ?? '0'), 10);
    const identifier = localStorage.getItem('IDENTIFIER') ?? '';
    const url = `${this._apiUrl}/api/v1/UserNotifications/client/${clientId}/userType/${userTypeId}/userIdentifier/${identifier}`;
    return this.authService.get(url).pipe(catchError(() => of(null)));
  }

  private _mapResponse(response: any): AppNotification[] {
    const raw: any[] = Array.isArray(response) ? response : (response.data || []);
    return raw.map(n => ({
      id: n.id,
      notificationSubject: n.notificationSubject,
      notificationContent: n.notificationContent,
      createdOn: n.createdOn ?? null,
      severity: n.severity,
      isRead: n.flagReadUnread === 1,
      timesago: n.timesago ?? ''
    }));
  }

  private _processIncoming(list: AppNotification[]): void {
    const newUnread = list.filter(n => !n.isRead && !this.seenIds.has(n.id));
    // Skip toasting on the very first poll — those are pre-existing notifications.
    if (newUnread.length > 0 && !this.firstPoll) {
      this.newNotifications$.next(newUnread);
    }
    this.firstPoll = false;
    list.forEach(n => this.seenIds.add(n.id));
    this.notifications$.next(list);
    this.unreadCount$.next(list.filter(n => !n.isRead).length);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
